import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    ChildColumnReference,
    IceGridReq,
    ParentColumnReference,
    IceDatabaseDetail,
    TableRefrences,
    selectedTable,
    selectedTableItems,
    FilterData,
    UpdateTableReq,
    DeleteReq,
    TableFields,
    Fields,
} from '../Modules/informate/models/ice-database.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { IceDetailsComponent } from '../Modules/informate/components/ice-details/ice-details.component';
import { IceapiService } from './iceapi.service';
import { WarningDialogComponent } from '../shared/info-dialogs/warning-dialog/warning-dialog.component';
import { WarningConfirmationDialogComponent } from '../shared/info-dialogs/warning-confirmation-dialog/warning-confirmation-dialog.component';
import { CustomToastService } from './custom-toast.service';
var DatabaseDetails = require('../../assets/json/IceDatabaseDetails.json');

@Injectable({
    providedIn: 'root',
})
export class IceDatabaseService {
    databaseDetails: IceDatabaseDetail[] = DatabaseDetails;
    tableRefrences: TableRefrences[];
    selectedTableItems = new BehaviorSubject<selectedTableItems>(null);
    selectedTableName = new BehaviorSubject<selectedTable>(null);

    constructor(
        private iceapiService: IceapiService,
        private ngxUiLoaderService: NgxUiLoaderService,
        private ngbModal: NgbModal,
        private customToastService: CustomToastService
    ) {}

    generateParentFields(data: TableRefrences[]): ParentColumnReference[] {
        const parentFileds = new Map();
        const output: ParentColumnReference[] = [];

        for (const entry of data) {
            const parentTableName = entry.parentTableName;
            const childColumnName = entry.childColumnName;
            const parentColumnName = entry.parentColumnName;

            let fieldName = parentTableName;
            let count = parentFileds.has(fieldName) ? parentFileds.get(fieldName) + 1 : 0;
            parentFileds.set(fieldName, count);

            if (count > 0) {
                fieldName += count;
            }

            output.push({ childColumnName, parentTableName, fieldName, parentColumnName });
        }
        return output;
    }

    generateChildFields(tableName: string): ChildColumnReference[] {
        const data = this.tableRefrences.filter((x) => x.parentTableName === tableName).map((x) => x.childTableName);
        const childFileds = new Map();
        const output: ChildColumnReference[] = [];

        for (const entry of data) {
            const originalFieldName = entry;
            let fieldName = entry;
            let parentFieldName = tableName;

            let count = childFileds.has(fieldName) ? childFileds.get(fieldName) + 1 : 0;
            childFileds.set(fieldName, count);

            if (count > 0) {
                fieldName += count;
                parentFieldName += count;
            }

            output.push({ fieldName, parentFieldName, originalFieldName });
        }
        return output;
    }

    generateTableFields(data: TableFields[]): Fields[] {
        const childFileds = new Map();
        const output: Fields[] = [];

        for (const entry of data) {
            const isRequire = entry.isNullable === 0 ? true : false;
            let field = entry.field.charAt(0).toLowerCase() + entry.field.slice(1);

            let count = childFileds.has(field) ? childFileds.get(field) + 1 : 0;
            let isComputed = entry.isComputed == 1 ? true : false;
            childFileds.set(field, count);

            if (count > 0) {
                field += count;
            }
            output.push({ field, isRequire, isComputed });
        }
        return output;
    }

    async getTableFields(selectedTable: string, childColumns: string[]): Promise<Fields[]> {
        this.ngxUiLoaderService.start();
        let fields: Fields[] = [];
        const res = await this.iceapiService.getTableFields(selectedTable).toPromise();
        if (res.success) {
            fields = this.generateTableFields(res.response)
                .concat(childColumns.sort().map((field) => ({ field: field, isRequire: false, isComputed: false })))
                .concat({ field: 'action', isRequire: false, isComputed: false });
        }
        this.ngxUiLoaderService.stop();
        return fields;
    }

    async getFilters(selectedTable: string): Promise<FilterData[]> {
        this.ngxUiLoaderService.start();
        let tableFilterList: FilterData[] = [];
        const res = await this.iceapiService.getFilterData(selectedTable).toPromise();

        if (res.success) {
            tableFilterList = res.response;
        }
        this.ngxUiLoaderService.stop();
        return tableFilterList;
    }

    async openParentDetails(columnName: string, rowData: any, parentReferences: ParentColumnReference[]): Promise<void> {
        this.ngxUiLoaderService.start();
        const columnReference = parentReferences.find((x) => x.fieldName == columnName);
        const selectedTableReferences = this.tableRefrences.filter((x) => x.childTableName === columnReference.parentTableName && x.childColumnName);
        const references = selectedTableReferences.map((x) => x.childColumnName + '_' + x.parentTableName).join(',');
        const parentColumnReferences = this.generateParentFields(selectedTableReferences);
        const childColumnReferences = this.generateChildFields(columnReference.parentTableName);
        const tableFields: Fields[] = await this.getTableFields(columnReference.parentTableName, childColumnReferences.map((x) => x.fieldName).sort());
        const tableFilterList = await this.getFilters(columnReference.parentTableName);

        const nullDataInserted = new Set();
        parentColumnReferences.forEach((reference) => {
            const field = tableFields.find((fieldObj) => fieldObj.field === reference.fieldName);
            if (field && !field.isRequire && !nullDataInserted.has(reference.parentTableName)) {
                const filterItem = tableFilterList.find((item) => item.title === reference.parentTableName);
                if (filterItem) {
                    filterItem.data.unshift({ id: null, value: 'Null' });
                    nullDataInserted.add(reference.parentTableName);
                }
            }
        });

        const payload: IceGridReq = {
            tableName: columnReference.parentTableName,
            pageNumber: 1,
            pageSize: 1,
            references: references,
            additionalWhere: `${columnReference.parentColumnName} = '${
                rowData[columnReference.childColumnName.charAt(0).toLowerCase() + columnReference.childColumnName.slice(1)]
            }'`,
            searchText: '',
        };

        this.iceapiService.getIceDatabaseGridData(payload).subscribe(
            (res) => {
                if (res.success) {
                    if (res.response.data.length > 0) {
                        const dialogRef = this.ngbModal.open(IceDetailsComponent, {
                            size: tableFields.length > 8 ? 'xl' : 'md',
                            backdrop: 'static',
                            keyboard: false,
                        });
                        dialogRef.componentInstance.type = 'Details';
                        dialogRef.componentInstance.tableName = columnReference.parentTableName;
                        dialogRef.componentInstance.rowData = res.response.data[0];
                        dialogRef.componentInstance.column = tableFields.slice(0, -1);
                        dialogRef.componentInstance.childColumnReferences = childColumnReferences;
                        dialogRef.componentInstance.parentColumnReferences = parentColumnReferences;
                        dialogRef.componentInstance.tableFilterList = tableFilterList;

                        dialogRef.result
                            .then((res) => {
                                if (res) {
                                    this.selectedTableName.next(res.data);
                                    this.ngbModal.dismissAll();
                                }
                            })
                            .catch((e) => {
                                console.warn(e);
                            });
                        this.ngxUiLoaderService.stop();
                    }
                }
            },
            (err) => {
                this.ngxUiLoaderService.stop();
            }
        );
    }

    deleteData(selectedTable: string, data: any, column: string[]): void {
        this.ngxUiLoaderService.start();
        const payload: DeleteReq = {
            id: data[column[0]],
            tableName: selectedTable,
        };

        this.iceapiService.CheckForDelete(payload).subscribe(
            (res) => {
                if (res.success) {
                    if (res.response.length) {
                        let dialogRef = this.ngbModal.open(WarningDialogComponent, {
                            size: 'md',
                            keyboard: false,
                            backdrop: 'static',
                        });
                        dialogRef.componentInstance.message = `Unable to delete the item. Please delete the dependent data from below tables before trying again.`;
                        dialogRef.componentInstance.childData = res.response;
                    } else {
                        let dialogRef = this.ngbModal.open(WarningConfirmationDialogComponent, {
                            size: 'sm',
                            keyboard: false,
                            backdrop: 'static',
                        });
                        dialogRef.componentInstance.modalMessage = `Are you sure you want to delete this item?`;
                        dialogRef.result
                            .then((result) => {
                                if (result) {
                                    let payload = {};
                                    payload[column[0]] = data[column[0]];
                                    const updatePayload: UpdateTableReq = {
                                        tableName: selectedTable,
                                        updatedTableData: JSON.stringify(payload),
                                        isDelete: true,
                                    };
                                    this.iceapiService.UpdateIceDatabaseTable(updatePayload).subscribe((res) => {
                                        if (res.success) {
                                            this.selectedTableName.next({ tableName: '' });
                                            this.selectedTableName.next({ tableName: selectedTable });
                                            this.customToastService.successToast('Item deleted successfully!', 'Success');
                                            this.ngbModal.dismissAll();
                                        } else {
                                            this.customToastService.errorToast(
                                                'Unable to delete the item. Please try again later or contact administrator.',
                                                'Error'
                                            );
                                        }
                                    });
                                }
                            })
                            .catch((error) => {
                                console.warn(error);
                            });
                    }
                }
                this.ngxUiLoaderService.stop();
            },
            (err) => {
                this.ngxUiLoaderService.stop();
            }
        );
    }
}
